import { useEffect, useCallback } from 'react';
import { useAppStore } from '../stores/app-store';

type Theme = 'light' | 'dark' | 'sepia' | 'system';

export function useTheme() {
  const settings = useAppStore((state) => state.settings);
  const updateSettings = useAppStore((state) => state.updateSettings);
  const theme = settings.theme;

  const applyTheme = useCallback((resolvedTheme: 'light' | 'dark' | 'sepia') => {
    const root = document.documentElement;

    // Remove all theme classes
    root.classList.remove('light', 'dark', 'sepia');

    // Add the new theme class
    root.classList.add(resolvedTheme);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      const colors = {
        light: '#ffffff',
        dark: '#0f1218',
        sepia: '#f5f0e1',
      };
      metaThemeColor.setAttribute('content', colors[resolvedTheme]);
    }
  }, []);

  const getSystemTheme = useCallback((): 'light' | 'dark' => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }, []);

  const resolveTheme = useCallback((): 'light' | 'dark' | 'sepia' => {
    if (theme === 'system') {
      return getSystemTheme();
    }
    return theme;
  }, [theme, getSystemTheme]);

  const setTheme = useCallback(
    (newTheme: Theme) => {
      updateSettings({ theme: newTheme });
    },
    [updateSettings]
  );

  // Apply theme on mount and when it changes
  useEffect(() => {
    applyTheme(resolveTheme());
  }, [theme, applyTheme, resolveTheme]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      applyTheme(getSystemTheme());
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, applyTheme, getSystemTheme]);

  return {
    theme,
    setTheme,
    resolvedTheme: resolveTheme(),
    systemTheme: getSystemTheme(),
  };
}
